import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InsightFilter, InsightOperator } from '@/shared/types/analytics';
import { Insight } from '@/shared/types/insights';

import { DEFAULT_OPERATORS, OPERATOR_LABELS } from './constants';

interface NumberInsightFilterProps {
  filterIndex: number;
  insight: Insight;
}

export const NumberInsightFilter = ({ filterIndex, insight }: NumberInsightFilterProps) => {
  const { getValues, control } = useFormContext();
  const { t } = useTranslation();
  const { update } = useFieldArray({ control, name: 'filters' });

  // Get the insightFilters array
  const insightFilters = useWatch({ control, name: `filters.${filterIndex}.insightFilters` }) || [];

  // Check if this insight already exists in the filters
  const existingFilterIndex = insightFilters.findIndex(
    (filter: InsightFilter) => filter.id === insight.name
  );

  const currentFilter =
    existingFilterIndex !== -1
      ? insightFilters[existingFilterIndex]
      : {
          id: insight.name,
          category: insight.type,
          operator: DEFAULT_OPERATORS[insight.type] || 'eq',
          values: [],
        };

  const updateFilter = (newInsightFilter: InsightFilter) => {
    const updatedFilters = [...insightFilters];

    if (existingFilterIndex !== -1) {
      // Update existing filter
      updatedFilters[existingFilterIndex] = newInsightFilter;
    } else {
      // Add new filter
      updatedFilters.push(newInsightFilter);
    }

    const currentFormFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
  };

  const handleOperatorChange = (newOperator: InsightOperator) => {
    updateFilter({ ...currentFilter, operator: newOperator });
  };

  const handleValueChange = (newValue: string) => {
    // Only process if user entered something
    if (newValue.trim().length > 0) {
      const numericValue = parseFloat(newValue);

      if (!isNaN(numericValue)) {
        // Valid number entered - update filter
        updateFilter({ ...currentFilter, values: [numericValue] });
      }
    } else if (existingFilterIndex !== -1) {
      // Input is empty and filter exists - remove it
      const updatedFilters = [...insightFilters];
      updatedFilters.splice(existingFilterIndex, 1);

      const currentFormFilter = getValues(`filters.${filterIndex}`);
      update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
    }
  };

  const operatorOptions = OPERATOR_LABELS[insight.type] || {};
  const isExistsOperator = currentFilter.operator === 'exists';

  return (
    <div className="space-y-3">
      <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
        <Select value={currentFilter.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(operatorOptions).map(([op, label]) => (
              <SelectItem key={op} value={op}>
                {t(label as string)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isExistsOperator ? (
        <Input
          type="number"
          value={currentFilter.values[0] ?? ''}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={t('filters.placeholders.number')}
          className="w-full"
        />
      ) : (
        <div className="rounded-md border border-border bg-subtle p-2 text-sm text-muted-foreground">
          {t('filters.exists_description', 'Any non-null value')}
        </div>
      )}
    </div>
  );
};
